import { useContext, useEffect, useRef, useState } from "react";
import { useSprings, animated, config, useSpring } from "@react-spring/web";
import { useDrag } from "@use-gesture/react";
import _ from "lodash";
import EditableContainer from "./EditableContainer";
import { AGGREGATION } from "../constants/pageTypes";
import AggregationPageLister from "./AggregationPageLister";
import { PagesManagementContext } from "../../routes/builderContexts";
import PageListerPanel from "./PageListerPanel";
import {
  LessonPagesActions,
  useLessonPagesDispatch,
  useLessonPagesState,
} from "../../contexts/LessonPagesProvider/LessonPagesProvider";
import WarningIcon from "../WarningIcon";

const ROW_HEIGHT_LARGE = 125;
const ROW_HEIGHT_SMALL = 50;
const STARTING_INDEX = 4;

function move(array, moveIndex, toIndex) {
  const item = array[moveIndex];
  const length = array.length;
  const diff = moveIndex - toIndex;

  if (diff > 0) {
    return [
      ...array.slice(0, toIndex),
      item,
      ...array.slice(toIndex, moveIndex),
      ...array.slice(moveIndex + 1, length),
    ];
  } else if (diff < 0) {
    const targetIndex = toIndex + 1;
    return [
      ...array.slice(0, moveIndex),
      ...array.slice(moveIndex + 1, targetIndex),
      item,
      ...array.slice(targetIndex, length),
    ];
  }
  return array;
}

const fn =
  (order, active = false, originalIndex = 0, curIndex = 0, y = 0) =>
  (index) => {
    return active && index === originalIndex
      ? {
          y: curIndex * ROW_HEIGHT_LARGE + y,
          scale: 1.1,
          zIndex: 1,
          shadow: 15,
          immediate: (key) => key === "zIndex",
          config: (key) => (key === "y" ? config.stiff : config.default),
        }
      : {
          y: order.indexOf(index) * ROW_HEIGHT_LARGE,
          scale: 1,
          zIndex: 0,
          shadow: 1,
          immediate: false,
        };
  };

// eslint-disable-next-line react/prop-types
export default function PageListerHelper({ lessonPages = [], updateArray, contentNumbering }) {
  const order = useRef(lessonPages.map((_, index) => index));
  const [isDragging, setIsDragging] = useState(false);
  const [pagesIconSprings, api] = useSprings(lessonPages.length, fn(order.current));
  const { switchCurrentlyDisplayedPage, currentlyDisplayedPageIndex, pagePullout, currentAggregationOrder } =
    useContext(PagesManagementContext);
  const lessonPageDispatch = useLessonPagesDispatch();
  const lessonPagesState = useLessonPagesState();
  const [expandedLessons, setExpandedLessons] = useState([]);

  useEffect(() => {
    const newOrder = lessonPages.map((_, index) => index);
    order.current = newOrder;

    api.start(fn(order.current));
  }, [lessonPages, api, order.current]);

  const bind = useDrag(
    ({ first, last, args: [originalIndex], active, movement: [, y] }) => {
      const curIndex = order.current.indexOf(originalIndex);

      if (first) {
        setIsDragging(true);
        // we switch to the page that we are dragging
        // to avoid setting an index out of bounds
        switchPage(curIndex);
      }

      const curRow = _.clamp(
        Math.round((curIndex * ROW_HEIGHT_LARGE + y) / ROW_HEIGHT_LARGE),
        0,
        lessonPages.length - 1,
      );
      const newOrder = move(order.current, curIndex, curRow);

      if (newOrder.every((o, i) => order.current[i] === o)) {
        return;
      }

      api.start(fn(newOrder, active, originalIndex, curIndex, y));

      if (!active) {
        const updatedPosition = newOrder.indexOf(originalIndex) - originalIndex;

        order.current = newOrder;

        const updatedPagesOrder = newOrder.map((index, i) => ({
          ...lessonPages.find((page) => page.uniquePageListerID === index),
          order: i + STARTING_INDEX,
          lessonPageIsDirty: true,
        }));

        if (currentAggregationOrder) {
          const updatedAggregationOrder = newOrder.findIndex((i) => i === curIndex) + STARTING_INDEX;
          lessonPageDispatch({
            type: LessonPagesActions.SET_CURRENT_AGGREGATION_ORDER,
            payload: updatedAggregationOrder,
          });

          updateArray(updatedPagesOrder, updatedAggregationOrder);

          lessonPageDispatch({
            type: LessonPagesActions.UPDATE_PAGE_ORDER,
            payload: {
              pageToGoTo: updatedAggregationOrder,
              pageIndex: currentAggregationOrder,
              isAggregation: true,
            },
          });

          return;
        }

        updateArray(updatedPagesOrder, currentlyDisplayedPageIndex + updatedPosition);

        lessonPageDispatch({
          type: LessonPagesActions.UPDATE_PAGE_ORDER,
          payload: {
            pageToGoTo: currentlyDisplayedPageIndex + updatedPosition,
            pageIndex: currentlyDisplayedPageIndex,
            isAggregation: false,
          },
        });

        if (last) {
          setIsDragging(false);
        }
      }
    },
    { threshold: 5 },
  );

  const getName = (springIndex) => lessonPages.find((el) => el.uniquePageListerID === springIndex)?.name;
  const getCurrentPage = (springIndex) => lessonPages.find((el) => el.uniquePageListerID === springIndex)?.order;
  const getPlayerType = (springIndex) =>
    lessonPages.find((el) => el.uniquePageListerID === springIndex)?.pagePlayerType;

  function switchPage(springIndex) {
    const pageOrder = getCurrentPage(springIndex);
    const playerType = getPlayerType(springIndex);

    // check if already on this page to avoid unnecessary calls
    if (playerType === AGGREGATION) {
      if (currentAggregationOrder === pageOrder && currentlyDisplayedPageIndex === 0) {
        return;
      }
      switchCurrentlyDisplayedPage(0, { aggregationOrder: pageOrder });
      return;
    }

    // for common pages, check if already on this page
    if (currentlyDisplayedPageIndex === pageOrder && currentAggregationOrder === null) {
      return;
    }

    switchCurrentlyDisplayedPage(pageOrder);
  }

  function updateName(newName, orderIndex) {
    // Update LessonPagesProvider first with the new name
    lessonPageDispatch({
      type: LessonPagesActions.UPDATE_PAGE_BY_ORDER,
      payload: {
        order: orderIndex,
        updates: { name: newName },
      },
    });

    // Since React state updates are async, we can't rely on reading
    // lessonPagesState immediately after dispatch. Instead, manually apply
    // the update to the current state to get the correct updated array.
    const updatedPagesFromProvider = lessonPagesState.lessonPages.map((page) => {
      if (page.order === orderIndex) {
        return { ...page, name: newName, lessonPageIsDirty: true };
      }
      return page;
    });

    updateArray(updatedPagesFromProvider);
  }

  const sliderSpring = useSpring({
    config: config.default,
    width: pagePullout ? 553 : 200,
    opacity: pagePullout ? 1 : 0,
    x: pagePullout ? 262 : 0,
  });

  function toggleLesson(springIndex) {
    const aggregationOrder = getCurrentPage(springIndex);

    if (expandedLessons.includes(aggregationOrder)) {
      setExpandedLessons((prev) => prev.filter((l) => l !== aggregationOrder));

      return;
    }

    const updatedExpandedLessons = [...expandedLessons, aggregationOrder];
    setExpandedLessons(updatedExpandedLessons);
  }

  function getExpandedOffset(maxOrder) {
    const expandedPages = lessonPages.filter(
      (lp) => lp.pagePlayerType === AGGREGATION && expandedLessons.includes(lp.order) && lp.order < maxOrder,
    );

    if (expandedPages.length) {
      return expandedPages.reduce((acc, prev) => prev.pageManifest.Pages.length + acc, 0) * ROW_HEIGHT_SMALL;
    }
  }

  const expandedAggregationsHeight = lessonPages
    .filter((lp) => lp.pagePlayerType === AGGREGATION && expandedLessons.includes(lp.order))
    .map((lp) => lp.pageManifest.Pages.length * ROW_HEIGHT_SMALL)
    .reduce((acc, prev) => acc + prev, 0);
  const containerHeight = lessonPages.length * ROW_HEIGHT_LARGE + expandedAggregationsHeight;

  return (
    <div className="side-panel-container" style={{ height: containerHeight }}>
      <div className="pages-icons-container">
        {pagesIconSprings.map(({ zIndex, shadow, y, scale }, springIndex) => {
          const isAggregation = getPlayerType(springIndex) === AGGREGATION;
          const currentPage = getCurrentPage(springIndex);
          const currentLessonPage = lessonPages.find((lp) => lp.order === currentPage);
          const isOutdated = typeof currentLessonPage?.isUpdated === "boolean" && currentLessonPage.isUpdated;
          const getClassName = () => {
            const isCurrentPage = order.current.indexOf(springIndex) === currentlyDisplayedPageIndex - STARTING_INDEX;

            if (isCurrentPage && !currentAggregationOrder) {
              return pagePullout ? "lesson-box selected" : "lesson-box selected-full";
            }

            return "lesson-box";
          };

          return (
            <animated.div
              onPointerUpCapture={() => {
                if (!isDragging) {
                  switchPage(springIndex);
                }
              }}
              {...bind(springIndex)}
              className={getClassName()}
              key={lessonPages[springIndex].name + springIndex}
              style={{
                zIndex,
                boxShadow: shadow.to((s) => `rgba(0, 0, 0, 0.15) 0px ${s}px ${2 * s}px 0px`),
                y,
                scale,
                position: "absolute",
                top: getExpandedOffset(currentPage),
              }}
            >
              {isAggregation ? (
                <AggregationPageLister
                  toggleExpand={() => {
                    toggleLesson(springIndex);
                  }}
                  aggregation={currentLessonPage}
                  expanded={expandedLessons.includes(currentPage)}
                  outdated={isOutdated}
                />
              ) : (
                <div className={`pageBox-container page-lister-${getPlayerType(springIndex)} divider-line`}>
                  <div className={`thumbnail thumbnail-container-${getPlayerType(springIndex)}`} />
                  <p>{`${getCurrentPage(springIndex) + 1}. `}</p>
                  <EditableContainer
                    updateName={updateName}
                    currentIndex={currentPage}
                    name={getName(springIndex)}
                    className="page-title"
                  >
                    {getName(springIndex)}
                  </EditableContainer>
                  <WarningIcon
                    visible={lessonPages[springIndex].pageManifest?.replacedAssets?.length > 0}
                    tooltipId="Page need review"
                  />
                </div>
              )}
              {pagePullout && !isAggregation && (
                <animated.div
                  style={{
                    transform: sliderSpring.x.to((x) => `translate3d(${x}px,-100%, -1px)`),
                    opacity: sliderSpring.opacity.to((opacity) => opacity),
                  }}
                  className={`info-panel ${springIndex}`}
                >
                  <div className="page-row">
                    <div className="mode-page-numbers-col">{contentNumbering[springIndex]}</div>
                    <PageListerPanel
                      updateArray={updateArray}
                      lessonPages={lessonPages}
                      currentSelected={currentPage}
                      selectedPage={order.current.indexOf(springIndex) === currentlyDisplayedPageIndex - STARTING_INDEX}
                    />
                  </div>
                </animated.div>
              )}
            </animated.div>
          );
        })}
      </div>
    </div>
  );
}
